import { clamp } from '../core/math';

/**
 * Procedural audio. No assets — everything is synthesised, which keeps the
 * bundle at zero extra bytes and lets the engine note track speed continuously
 * rather than crossfading between samples.
 *
 * Sound does a lot of the work of communicating grip state that the HUD
 * otherwise has to spell out in words: you hear the rears let go before you see
 * the LOCKED caption.
 */

export interface AudioState {
  speed: number; // m/s
  /** 0..1 of idle-to-redline. Direct drive: this is speed itself, rescaled —
   *  see IDLE_RPM/REDLINE_RPM in physics/constants.ts. No gearbox means no
   *  shift point, so the note climbs continuously with it. */
  rpm01: number;
  throttle: number;
  slip: number; // |lateral velocity|, m/s
  locked: boolean;
  onKerb: boolean;
  offTrack: boolean;
}

/** Shared white-noise buffer; regenerating per voice would be wasteful. */
function makeNoise(ctx: AudioContext): AudioBuffer {
  const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

export class GameAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;

  private engineOsc: OscillatorNode | null = null;
  private engineSub: OscillatorNode | null = null;
  /** Odd-harmonic layer above the fundamental — the buzz that separates a
   *  lean two-stroke snarl from a muffled four-stroke drone. */
  private engineBuzz: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;

  private scrubGain: GainNode | null = null;
  private scrubFilter: BiquadFilterNode | null = null;
  private rumbleGain: GainNode | null = null;
  private rumbleFilter: BiquadFilterNode | null = null;
  /** Wind past an open cockpit — the loudest thing in the helmet by the time
   *  a kart is doing 100 km/h, and absent before this entirely. Its filter is
   *  fixed at creation and never re-touched, so only the gain node persists. */
  private windGain: GainNode | null = null;

  private started = false;
  private enabled = true;
  private volume = 0.7;

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(on ? this.volume : 0, this.ctx.currentTime, 0.05);
    }
  }

  setVolume(v: number): void {
    this.volume = clamp(v, 0, 1);
    if (this.master && this.ctx && this.enabled) {
      this.master.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.05);
    }
  }

  /** Must be called from a user gesture; browsers block audio otherwise. */
  start(): void {
    if (this.started) {
      void this.ctx?.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    const ctx = new Ctor();
    this.ctx = ctx;
    this.started = true;

    const master = ctx.createGain();
    master.gain.value = this.enabled ? this.volume : 0;
    master.connect(ctx.destination);
    this.master = master;

    // --- engine: single-cylinder two-stroke, not a muffled four-stroke -----
    // A saw carries the fundamental, a sub an octave down gives it body, and
    // a square three octaves-and-a-fifth up (x3) is the odd-harmonic buzz
    // that actually makes it read as a screaming 125cc rather than a lawnmower.
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = 60;
    const sub = ctx.createOscillator();
    sub.type = 'square';
    sub.frequency.value = 30;
    const buzz = ctx.createOscillator();
    buzz.type = 'square';
    buzz.frequency.value = 180;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 900;
    filter.Q.value = 4;

    const gain = ctx.createGain();
    gain.gain.value = 0;
    const buzzGain = ctx.createGain();
    buzzGain.gain.value = 0.5;

    osc.connect(filter);
    sub.connect(filter);
    buzz.connect(buzzGain);
    buzzGain.connect(filter);
    filter.connect(gain);
    gain.connect(master);
    osc.start();
    sub.start();
    buzz.start();

    this.engineOsc = osc;
    this.engineSub = sub;
    this.engineBuzz = buzz;
    this.engineFilter = filter;
    this.engineGain = gain;

    // --- tyre scrub: bandpassed noise, gain follows slip -------------------
    const noise = makeNoise(ctx);

    const scrubSrc = ctx.createBufferSource();
    scrubSrc.buffer = noise;
    scrubSrc.loop = true;
    const scrubFilter = ctx.createBiquadFilter();
    scrubFilter.type = 'bandpass';
    scrubFilter.frequency.value = 1600;
    scrubFilter.Q.value = 1.4;
    const scrubGain = ctx.createGain();
    scrubGain.gain.value = 0;
    scrubSrc.connect(scrubFilter);
    scrubFilter.connect(scrubGain);
    scrubGain.connect(master);
    scrubSrc.start();
    this.scrubFilter = scrubFilter;
    this.scrubGain = scrubGain;

    // --- rumble: low noise for kerbs and grass -----------------------------
    const rumbleSrc = ctx.createBufferSource();
    rumbleSrc.buffer = noise;
    rumbleSrc.loop = true;
    const rumbleFilter = ctx.createBiquadFilter();
    rumbleFilter.type = 'lowpass';
    rumbleFilter.frequency.value = 220;
    const rumbleGain = ctx.createGain();
    rumbleGain.gain.value = 0;
    rumbleSrc.connect(rumbleFilter);
    rumbleFilter.connect(rumbleGain);
    rumbleGain.connect(master);
    rumbleSrc.start();
    this.rumbleFilter = rumbleFilter;
    this.rumbleGain = rumbleGain;

    // --- wind: highpassed noise, gain climbs with the square of speed ------
    // An open kart has no windscreen and the driver's helmet sits right in
    // the airflow, so this is not a background texture at speed — it becomes
    // the loudest thing in the mix, the way it actually is.
    const windSrc = ctx.createBufferSource();
    windSrc.buffer = noise;
    windSrc.loop = true;
    const windFilter = ctx.createBiquadFilter();
    windFilter.type = 'highpass';
    windFilter.frequency.value = 1100;
    windFilter.Q.value = 0.7;
    const windGain = ctx.createGain();
    windGain.gain.value = 0;
    windSrc.connect(windFilter);
    windFilter.connect(windGain);
    windGain.connect(master);
    windSrc.start();
    this.windGain = windGain;
  }

  stop(): void {
    void this.ctx?.suspend();
  }

  update(s: AudioState): void {
    const ctx = this.ctx;
    if (!ctx || !this.engineOsc || !this.engineGain || !this.engineFilter) return;
    const t = ctx.currentTime;
    const smooth = 0.04;

    // Direct drive: revs are speed itself, rescaled — no clutch or ratio to
    // slip, so nothing here ever resets or "shifts". `rpm01` already carries
    // that rescaling (see AudioState's own doc).
    const rpm = s.rpm01;

    this.engineOsc.frequency.setTargetAtTime(35 + rpm * 175, t, smooth);
    this.engineSub?.frequency.setTargetAtTime(18 + rpm * 88, t, smooth);
    this.engineBuzz?.frequency.setTargetAtTime((35 + rpm * 175) * 3, t, smooth);
    this.engineFilter.frequency.setTargetAtTime(900 + rpm * 3200 + s.throttle * 700, t, smooth);
    this.engineGain.gain.setTargetAtTime(0.05 + s.throttle * 0.06 + rpm * 0.04, t, smooth);

    if (this.scrubGain && this.scrubFilter) {
      // A lock-up is louder and much brighter than ordinary scrub.
      const slip = clamp(s.slip / 7, 0, 1);
      const target = s.locked ? 0.16 : slip * 0.1;
      this.scrubGain.gain.setTargetAtTime(target, t, 0.03);
      this.scrubFilter.frequency.setTargetAtTime(s.locked ? 3200 : 1300 + slip * 900, t, 0.05);
    }

    if (this.rumbleGain && this.rumbleFilter) {
      const speedScale = clamp(s.speed / 20, 0, 1);
      const target = s.offTrack ? 0.14 * speedScale : s.onKerb ? 0.09 * speedScale : 0;
      this.rumbleGain.gain.setTargetAtTime(target, t, 0.04);
      this.rumbleFilter.frequency.setTargetAtTime(s.offTrack ? 400 : 180, t, 0.05);
    }

    if (this.windGain) {
      // Aerodynamic drag itself is quadratic in speed (see DRAG_K in
      // constants.ts) — the noise it makes follows the same curve, which is
      // why it stays a background hiss until well past half speed and then
      // rises fast enough to fight the engine for attention.
      const windTarget = 0.22 * rpm * rpm;
      this.windGain.gain.setTargetAtTime(windTarget, t, 0.08);
    }
  }

  /** Short blip for UI events — lap start, personal best. */
  blip(frequency = 660, duration = 0.12): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.enabled) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(this.master);
    osc.start();
    osc.stop(ctx.currentTime + duration + 0.02);
  }

  /** A low impact bash for a barrier hit. Intensity 0..1 scales volume. */
  thud(intensity: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.enabled) return;
    const t = ctx.currentTime;
    const dur = 0.16;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(140, t);
    osc.frequency.exponentialRampToValueAtTime(55, t + dur);
    filter.type = 'lowpass';
    filter.frequency.value = 700;
    const peak = 0.12 + clamp(intensity, 0, 1) * 0.4;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(peak, t + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  /** Silences the rolling voices without tearing the graph down. */
  idle(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    this.engineGain?.gain.setTargetAtTime(0, ctx.currentTime, 0.08);
    this.scrubGain?.gain.setTargetAtTime(0, ctx.currentTime, 0.08);
    this.rumbleGain?.gain.setTargetAtTime(0, ctx.currentTime, 0.08);
    this.windGain?.gain.setTargetAtTime(0, ctx.currentTime, 0.08);
  }
}
